import { useEffect } from "react";

const DEFAULTS = {
  title: "Official Morning Teer | Morning Teer Official Result",
  description:
    "Check Morning Teer Result Today and Shillong Morning Teer FR and SR results. View common numbers, target numbers, house ending, previous results and result charts.",
  siteName: "Official Morning Teer",
  baseUrl: "https://officialmorningteer.com",
  ogImage: "https://officialmorningteer.com/og-image.png",
};

function setMeta(name, content, isProperty = false) {
  const key = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${key}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setStructuredData(id, obj) {
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

function removeStructuredData(id) {
  const el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (el) el.remove();
}

/**
 * Sets title, meta description, canonical, OG + Twitter tags, and optional
 * BreadcrumbList JSON-LD for the current route. Restores previous values on unmount.
 */
export default function useSeo({
  title,
  ogTitle,
  description,
  ogDescription,
  path = "/",
  ogImage = DEFAULTS.ogImage,
  robots = "index, follow, max-image-preview:large",
  breadcrumbs, // Array<{ name, path }>
  ldId = "page-ld",
} = {}) {
  useEffect(() => {
    const fullTitle = title || DEFAULTS.title;
    const shareTitle = ogTitle || fullTitle;
    const desc = description || DEFAULTS.description;
    const shareDesc = ogDescription || desc;
    const canonical = `${DEFAULTS.baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    document.title = fullTitle;
    setMeta("description", desc);
    setMeta("robots", robots);
    setLink("canonical", canonical);

    // Open Graph
    setMeta("og:type", "website", true);
    setMeta("og:site_name", DEFAULTS.siteName, true);
    setMeta("og:url", canonical, true);
    setMeta("og:title", shareTitle, true);
    setMeta("og:description", shareDesc, true);
    setMeta("og:image", ogImage, true);
    setMeta("og:locale", "en_IN", true);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", shareTitle);
    setMeta("twitter:description", shareDesc);
    setMeta("twitter:image", ogImage);

    // Optional breadcrumbs
    if (breadcrumbs && breadcrumbs.length) {
      setStructuredData("breadcrumbs", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${DEFAULTS.baseUrl}${b.path.startsWith("/") ? b.path : "/" + b.path}`,
        })),
      });
    } else {
      removeStructuredData("breadcrumbs");
    }

    return () => {
      // Cleanup breadcrumbs on unmount — page-specific
      removeStructuredData("breadcrumbs");
    };
  }, [title, description, path, ogImage, robots, JSON.stringify(breadcrumbs)]);
}

export const SEO_DEFAULTS = DEFAULTS;
